import React from "react";
import { motion, AnimatePresence } from "framer-motion";

import img1 from "@/assets/member/img1.webp";
import img2 from "@/assets/member/img2.webp";
import img3 from "@/assets/member/img3.webp";
import img4 from "@/assets/member/img4.webp";
import img5 from "@/assets/member/img5.webp";
import img6 from "@/assets/member/img6.webp";
import img7 from "@/assets/member/img7.webp";

const IMAGE_MAP: Record<string, string> = {
  "img1.webp": img1,
  "img2.webp": img2,
  "img3.webp": img3,
  "img4.webp": img4,
  "img5.webp": img5,
  "img6.webp": img6,
  "img7.webp": img7,
};

interface Member {
  id: number;
  role: string;
  name: string;
  experience: string[];
  capability: string[];
  image: string;
}

interface MemberCardProps {
  member: Member;
  isExpanded: boolean;
  isMobile: boolean;
  onToggle: () => void;
}

export function MemberCard({ member, isExpanded, isMobile, onToggle }: MemberCardProps) {
  const fileName = member.image.split("/").pop() || "";
  const imageSrc = IMAGE_MAP[fileName] || member.image;

  const COLLAPSED_HEIGHT = isMobile ? 400 : 420;
  const EXPANDED_HEIGHT = isMobile ? 380 : 400;

  const springTransition = { type: "spring" as const, stiffness: 260, damping: 32 };

  return (
    <div className="relative flex w-full flex-col items-center">
      {/* 사진 카드 메인 - 너비는 부모 motion.div를 따름 (w-full) */}
      <motion.div
        layout
        onClick={onToggle}
        className={`relative z-20 w-full cursor-pointer overflow-hidden shadow-lg ${
          isExpanded 
            ? "rounded-t-[20px] rounded-b-none shadow-2xl" 
            : "rounded-[20px]"
        }`}
        style={{
          height: isExpanded ? EXPANDED_HEIGHT : COLLAPSED_HEIGHT,
        }}
        transition={springTransition}
      >
        <motion.img
          layout
          src={imageSrc}
          alt={member.name}
          className="h-full w-full object-cover"
          initial={false}
        />
        
        <AnimatePresence mode="wait">
          {!isExpanded && (
            <motion.div 
              key="overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-black/80 via-black/20 to-transparent p-6"
            >
              <span className="text-[12px] font-medium tracking-[0.08em] text-white/80 uppercase">
                {member.role}
              </span>
              <span className="text-[20px] md:text-[22px] font-bold text-white mt-0.5">
                {member.name}
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* 하단 흰색 상세 정보 칸 */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            layout
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="relative z-10 w-full overflow-hidden rounded-b-[20px] bg-white text-left shadow-2xl"
            transition={springTransition}
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.15 }}
              className="p-5 md:p-6"
            >
              <div className="mb-4">
                <h3 className="text-[10px] md:text-[11px] font-bold text-[#1A7AFF] uppercase tracking-[0.12em]">{member.role}</h3>
                <h2 className="text-[24px] md:text-[26px] font-bold text-[#111111] leading-tight mt-0.5">{member.name}</h2>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h4 className="text-[9px] md:text-[10px] font-bold text-gray-400 uppercase mb-1.5 tracking-widest">경력</h4>
                  <ul className="text-[12px] md:text-[13px] text-gray-700 space-y-1 leading-snug">
                    {member.experience.map((exp, idx) => (
                      <li key={idx} className="flex gap-1.5">
                        <span className="text-[#1A7AFF]">•</span>
                        <span>{exp}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div>
                  <h4 className="text-[9px] md:text-[10px] font-bold text-gray-400 uppercase mb-1.5 tracking-widest">역량</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {member.capability.map((cap, idx) => (
                      <span key={idx} className="rounded-md bg-gray-100 px-2 py-1 text-[10px] md:text-[11px] font-semibold text-gray-600">
                        {cap}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
